import React, { useCallback, useMemo, useState } from 'react';
import '@patternfly/react-styles/css/utilities/Sizing/sizing.css';
import { useHistory } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';
import createQueryRequest from 'services/createQueryRequest';
import useAPI from 'services/useAPI';
import useApiCall from 'services/useApiCall';
import useAlerts from 'utils/hooks/useAlerts';
import ContentPage from 'components/ContentView/ContentPage/ContentPage';
import { useTranslation } from 'localization/useTranslation';

import DeleteAsset from './DeleteAsset';
import { DATA_SOURCE } from './WizardConstants';
import { BindingsStep } from './BindingsStep';
import WizardStepReview from './components/WizardStepReview';
import WizardStepAssetDetail from './components/WizardStepAssetDetails';
import WizardStepOrganization from './components/WizardStepOrganization';
import GlazeWizard from 'components/Wizard/GlazeWizard';
import { PROVIDER_TYPE } from './WizardConstants';
import { modelKeys, useDefaultBinding } from '../Model/queries';
import { useAuthService } from 'services/AuthService/AuthService';
import _ from 'lodash';


const AdminCreateAsset = ({ breadcrumbs }) => {
  const api = useAPI();
  const history = useHistory();
  const T = useTranslation();
  const { user } = useAuthService();
  const [alerts, , addErrorAlert] = useAlerts();
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const [assetInfo, setAssetInfo] = useState({
    assetId: '',
    asset: {},
    assetData: {},
    currentOrganization: _.first(user?.effectiveOrgs),
    model: {},
    // provider: {},
    providerClient: {
      provider: { id: '' },
      template: { id: '' }
    }
    // providerClientTemplate: {}
  });
  const steps = useMemo(() => {
    return [
      {
        name: T.asset.details,
        component: WizardStepAssetDetail
      },
      {
        name: T.common.bindings,
        component: BindingsStep
      },
      {
        name: T.common.organization,
        component: WizardStepOrganization,
        isHidden: _.size(user?.organizations) === 1 ? true : false
      },
      {
        name: T.admin.assetwizard.review,
        component: WizardStepReview
      }
    ];
  }, [T.admin.assetwizard.review, T.asset.details, T.common.bindings, T.common.organization, user?.organizations]);


  const [bindAsset, isBindingAsset] = useApiCall({
    apiCall: api.core.bindAsset,
    onSuccess: useCallback(() => {
      history.push(`${assetInfo.asset.id}`);
    }, [assetInfo.asset, history]),
    onError: useCallback(() => {

    }, [])
  });

  const { isCreating: isCreatingBinding } = useDefaultBinding({ createOnSuccess: useCallback(() => {
    switch (assetInfo.dataSource) {
      case DATA_SOURCE.provider:
        bindAsset(assetInfo?.asset?.id, {
          autobindRemoteInstanceIds: [
            `${assetInfo?.providerClient?.provider?.id}::${assetInfo?.providerClient?.id}`
          ]
        });
        break;
      case DATA_SOURCE.asset:
        bindAsset(assetInfo?.asset?.id, {
          autobindEndpointInstanceId: assetInfo?.assetData?.source?.id,
          autobindBindingDescriptionId: assetInfo?.assetData?.bindingModel?.id
        });
        break;
      default:
        console.error('we should not be here');
    }
  }, [assetInfo?.asset?.id, assetInfo?.assetData?.bindingModel?.id, assetInfo?.assetData?.source?.id, assetInfo.dataSource, assetInfo?.providerClient?.id, assetInfo?.providerClient?.provider?.id, bindAsset])
  });

  const [createAsset, isCreatingAsset] = useApiCall({
    apiCall: api.core.createAsset,
    onSuccess: useCallback(() => {
      switch (assetInfo.dataSource) {
        case DATA_SOURCE.provider:
          bindAsset(assetInfo?.asset?.id, {
            autobindRemoteInstanceIds: [
              `${assetInfo?.providerClient?.provider?.id}::${assetInfo?.providerClient?.id}`
            ]
          });
          break;
        case DATA_SOURCE.asset:
          console.error('binding asset not ready yet');
          // bindAsset(assetInfo?.asset?.id, {
          //   autobindEndpointInstanceId: assetInfo?.assetData?.source?.id,
          //   autobindBindingDescriptionId: assetInfo?.assetData?.bindingModel?.id
          // });
          break;
        default:
          console.error('we should not be here');
      }
    }, [assetInfo?.asset?.id, assetInfo.dataSource, assetInfo?.providerClient?.id, assetInfo?.providerClient?.provider?.id, bindAsset]),
    onError: useCallback((error) => {
      addErrorAlert(error);
      console.error(error);
    }, [addErrorAlert])
  });

  const [provisionProviderClient, applyingProviderClientTemplate] = useApiCall({
    apiCall: api.core.provisionProviderClient,
    onSuccess: useCallback(() => {
      const organizationId = assetInfo?.currentOrganization?.id ?? _.first(user?.effectiveOrgs)?.id;
      createAsset({
        id: assetInfo.asset.id,
        modelId: assetInfo.asset?.model?.id,
        displayName: assetInfo.asset.displayName,
        description: assetInfo.asset.description,
        organizationId
      });
    }, [assetInfo.asset.description, assetInfo.asset.displayName, assetInfo.asset.id, assetInfo.asset?.model?.id, assetInfo?.currentOrganization?.id, createAsset, user?.effectiveOrgs]),
    onError: useCallback((error) => {
      addErrorAlert(error);
    }, [addErrorAlert])
  });

  const modelsQuery = useQuery(modelKeys.models(), createQueryRequest(api.core.getModels));
  const assetModels = useMemo(() => {
    return modelsQuery.data;
  }, [modelsQuery.data]);
  const isLoadingModels = modelsQuery.isLoading;


  const doProvisionProviderClient = useCallback(() => {
    var options;
    if (assetInfo?.providerClient?.provider?.type === PROVIDER_TYPE.iothub) {
      if (assetInfo?.providerClient?.template?.options?.isEdge) {
        options = {
          EdgeDeviceProvisioningOptions: {},
          ...assetInfo?.providerClient?.template?.options
        };
      } else {
        options = {
          IoTDeviceProvisioningOptions: {},
          ...assetInfo?.providerClient?.template?.options
        };
      }
    } else {
      options = {
        ...assetInfo?.providerClient?.template?.options
      };
    }
    const templateData = {
      templateId: assetInfo?.providerClient?.template?.id,
      options
    };
    provisionProviderClient(assetInfo?.providerClient?.provider?.id, assetInfo?.providerClient?.id, templateData);
  }, [assetInfo?.providerClient?.id, assetInfo?.providerClient?.provider?.id, assetInfo?.providerClient?.provider?.type, assetInfo?.providerClient?.template?.id, assetInfo?.providerClient?.template?.options, provisionProviderClient]);

  const [createProviderClient, isCreatingProviderClient] = useApiCall({
    apiCall: api.core.createProviderClient,
    onSuccess: useCallback(() => {
      doProvisionProviderClient();
    }, [doProvisionProviderClient]),
    onError: useCallback((error) => {
      addErrorAlert(error);
    }, [addErrorAlert])
  });


  const onCreate = useCallback(() => {
    const organizationId = assetInfo?.currentOrganization?.id ?? _.first(user?.effectiveOrgs)?.id;
    switch (assetInfo.dataSource) {
      case DATA_SOURCE.provider:
        if (assetInfo.linkToExistingProviderClient) {
          createAsset({
            id: assetInfo.asset.id,
            modelId: assetInfo.asset?.model?.id,
            displayName: assetInfo.asset.displayName,
            description: assetInfo.asset.description,
            organizationId
          });
        } else {
          const providerClient = {
            ..._.pick(assetInfo.providerClient, ['id', 'displayName', 'description', 'enbaled']),
            organizationId
          };
          createProviderClient(assetInfo.providerClient?.provider?.id, providerClient);
        }
        break;
      case DATA_SOURCE.asset:
        createAsset({
          id: assetInfo.asset?.id,
          modelId: assetInfo.asset?.model?.id,
          displayName: assetInfo.asset?.displayName,
          description: assetInfo.asset?.description,
          organizationId
        });
        break;
      default:
        console.error('should not happen');
    }
  }, [assetInfo.asset.description, assetInfo.asset.displayName, assetInfo.asset.id, assetInfo.asset?.model?.id, assetInfo?.currentOrganization?.id, assetInfo.dataSource, assetInfo.linkToExistingProviderClient, assetInfo.providerClient, createAsset, createProviderClient, user?.effectiveOrgs]);

  const componentProps = useMemo(() => {
    return {
      assetModels,
      isLoadingAssetModels: isLoadingModels,
      assetInfo,
      setAssetInfo
    };
  }, [assetInfo, assetModels, isLoadingModels]);

  const isCreating = isCreatingProviderClient || applyingProviderClientTemplate || isCreatingAsset || isBindingAsset || isCreatingBinding;
  return (
    <ContentPage breadcrumb={breadcrumbs} help={'admin/assets/create'}>
      { alerts }
      <GlazeWizard
        steps={steps}
        onClose={() => history.push('/admin/assets')}
        onError={addErrorAlert}
        onAction={onCreate}
        isActionExecuting={isCreating}
        componentProps={componentProps} />
      <DeleteAsset
        confirmDeleteItem={confirmDeleteItem}
        onDelete={() => history.push('/admin/assets')}
        cancelDelete={() => {
          setConfirmDeleteItem(null);
        }} />
    </ContentPage>
  );
};

export default AdminCreateAsset;
